/**
 * Trend chart grid for Cached Performance (CR-4751).
 *
 * Layout mirrors DefaultTab:
 *  - Row 1: PPM + OTIF (each with target line)
 *  - Row 2: Combined Financial (open orders + spend + arrears, dual Y-axis) + OTD
 *  - Row 3: NCRs bar chart + Performance Summary card
 */
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ComposedChart, ReferenceLine,
} from 'recharts'
import { Info } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card'
import { ChartWrapper } from '../ChartWrapper'
import { formatCompactNumber } from '../../../lib/utils'
import { useFormatters } from '../../../contexts/PreferencesContext'
import type {
  MetricType, DashboardSnapshot, TrendPoint,
} from '../../../lib/api/dashboard-snapshots'
import type { PerformanceTargets } from '../../../hooks/usePerformanceHistory'
import { asNumber, mergeSeries, toChartSeries } from './helpers'

function ChartEmpty({ reason }: { reason: string }) {
  return (
    <div className="h-[200px] flex flex-col items-center justify-center text-muted-foreground gap-2">
      <Info className="w-6 h-6" />
      <p className="text-sm">{reason}</p>
    </div>
  )
}

interface CachedTrendChartsProps {
  trends: Record<MetricType, TrendPoint[]>
  latestByMetric: Map<MetricType, DashboardSnapshot>
  targets: PerformanceTargets
  period: 'daily' | 'weekly' | 'monthly' | 'quarterly'
}

export function CachedTrendCharts({ trends, latestByMetric, targets, period }: CachedTrendChartsProps) {
  const { preferences } = useFormatters()
  const ppmSeries = toChartSeries(trends.ppm ?? [], period)
  const otifSeries = toChartSeries(trends.otif_percentage ?? [], period)
  const otdSeries = toChartSeries(trends.otd_percentage ?? [], period)
  const ncrSeries = toChartSeries(trends.ncr_count ?? [], period)

  // Combined Financial chart (open orders + spend + arrears), dual Y-axis
  const financialMerged = mergeSeries([
    { key: 'open_orders', points: trends.open_order_value ?? [] },
    { key: 'spend', points: trends.monthly_spend ?? [] },
    { key: 'arrears', points: trends.arrears_value ?? [] },
  ], period)

  return (
    <>
      {/* Row 1: PPM + OTIF */}
      <div className="grid gap-4 grid-cols-2">
        <ChartWrapper
          title="PPM" data={ppmSeries} exportFilename="cached-ppm-trend"
          legend={[
            { color: '#3b82f6', label: 'PPM', dataKey: 'value' },
            ...(targets.ppm !== null ? [{ color: '#ef4444', label: 'Target', dataKey: 'target' }] : []),
          ]}>
          {ppmSeries.length === 0 ? <ChartEmpty reason="No PPM snapshots yet." /> : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={ppmSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="PPM" />
                {targets.ppm !== null && (
                  <ReferenceLine y={targets.ppm} stroke="#ef4444" strokeDasharray="4 4" strokeWidth={2}
                    label={{ value: `Target ${targets.ppm}`, position: 'right', fill: '#ef4444', fontSize: 10 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartWrapper>

        <ChartWrapper
          title="OTIF %" data={otifSeries} exportFilename="cached-otif-trend"
          legend={[
            { color: '#1d4ed8', label: 'OTIF %', dataKey: 'value' },
            ...(targets.otif !== null ? [{ color: '#dc2626', label: 'Target', dataKey: 'target' }] : []),
          ]}>
          {otifSeries.length === 0 ? <ChartEmpty reason="No OTIF snapshots yet." /> : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={otifSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(v: number) => `${v.toFixed(1)}%`} />
                <Line type="monotone" dataKey="value" stroke="#1d4ed8" strokeWidth={3} dot={{ r: 5, fill: '#1d4ed8', stroke: '#fff', strokeWidth: 2 }} name="OTIF %" />
                {targets.otif !== null && (
                  <ReferenceLine y={targets.otif} stroke="#dc2626" strokeDasharray="4 4" strokeWidth={2}
                    label={{ value: `Target ${targets.otif}%`, position: 'right', fill: '#dc2626', fontSize: 10 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartWrapper>
      </div>

      {/* Row 2: Combined Financial (dual Y-axis) + OTD */}
      <div className="grid gap-4 grid-cols-2">
        <ChartWrapper
          title={`Financial Trends (${preferences.currency})`} data={financialMerged} exportFilename="cached-financial-trend"
          legend={[
            { color: '#2563eb', label: 'Open Orders', dataKey: 'open_orders' },
            { color: '#059669', label: 'Monthly Spend', dataKey: 'spend' },
            { color: '#dc2626', label: 'Arrears', dataKey: 'arrears' },
          ]}>
          {financialMerged.length === 0 ? <ChartEmpty reason="No financial snapshots yet." /> : (
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={financialMerged}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={formatCompactNumber} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={formatCompactNumber} />
                <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(v: number) => v.toLocaleString()} />
                <Line yAxisId="left" type="monotone" dataKey="open_orders" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} name="Open Orders" />
                <Line yAxisId="left" type="monotone" dataKey="spend" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="Monthly Spend" />
                <Line yAxisId="right" type="monotone" dataKey="arrears" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} name="Arrears" />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartWrapper>

        <ChartWrapper
          title="OTD %" data={otdSeries} exportFilename="cached-otd-trend"
          legend={[
            { color: '#1d4ed8', label: 'OTD %', dataKey: 'value' },
            ...(targets.otd !== null ? [{ color: '#dc2626', label: 'Target', dataKey: 'target' }] : []),
          ]}>
          {otdSeries.length === 0 ? <ChartEmpty reason="No OTD snapshots yet." /> : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={otdSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(v: number) => `${v.toFixed(1)}%`} />
                <Line type="monotone" dataKey="value" stroke="#1d4ed8" strokeWidth={3} dot={{ r: 5, fill: '#1d4ed8', stroke: '#fff', strokeWidth: 2 }} name="OTD %" />
                {targets.otd !== null && (
                  <ReferenceLine y={targets.otd} stroke="#dc2626" strokeDasharray="4 4" strokeWidth={2}
                    label={{ value: `Target ${targets.otd}%`, position: 'right', fill: '#dc2626', fontSize: 10 }} />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartWrapper>
      </div>

      {/* Row 3: NCRs + Performance Summary */}
      <div className="grid grid-cols-2 gap-4">
        <ChartWrapper
          title="NCRs Raised" data={ncrSeries} exportFilename="cached-ncrs-trend"
          legend={[{ color: '#f59e0b', label: 'NCRs', dataKey: 'value' }]}>
          {ncrSeries.length === 0 ? <ChartEmpty reason="No NCR snapshots yet." /> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={ncrSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                <Bar dataKey="value" fill="#f59e0b" name="NCRs" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartWrapper>

        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Performance Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 h-[200px] items-center">
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Total GRNs</p>
                <p className="text-2xl font-bold">
                  {asNumber(latestByMetric.get('total_grns')?.metric_value).toLocaleString()}
                </p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Open NCRs</p>
                <p className="text-2xl font-bold">
                  {asNumber(latestByMetric.get('ncr_open_count')?.metric_value).toLocaleString()}
                </p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Active Customers</p>
                <p className="text-2xl font-bold">
                  {asNumber(latestByMetric.get('active_customer_count')?.metric_value).toLocaleString()}
                </p>
              </div>
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Arrears Lines</p>
                <p className="text-2xl font-bold">
                  {asNumber(latestByMetric.get('arrears_count')?.metric_value).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
